import { 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import { secureLog } from '../lib/errors/logger';
import { COLLECTIONS } from '../constants/collections';

// User interaction types
export type UserInteractionType = 
  | 'product_view'
  | 'product_click'
  | 'category_browse'
  | 'search_query'
  | 'add_to_cart'
  | 'add_to_wishlist'
  | 'remove_from_cart'
  | 'remove_from_wishlist'
  | 'purchase'
  | 'page_view'
  | 'session_start'
  | 'session_end';

// User interaction interface
export interface UserInteraction {
  id?: string;
  userId: string;
  sessionId: string;
  interactionType: UserInteractionType;
  productId?: string;
  categoryId?: string;
  categoryName?: string;
  searchQuery?: string;
  pageUrl?: string;
  location?: {
    city?: string;
    region?: string;
    country?: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
    ipAddress?: string;
  };
  metadata?: {
    productTitle?: string;
    productPrice?: number;
    productCategory?: string;
    userAgent?: string;
    referrer?: string;
    duration?: number; // Time spent on page in seconds
  };
}

// User behavior analytics interface
export interface UserBehaviorAnalytics {
  userId: string;
  totalInteractions: number;
  productViews: number;
  categoryBrowses: number;
  searchQueries: number;
  cartAdditions: number;
  wishlistAdditions: number;
  purchases: number;
  lastActive: Timestamp;
  favoriteCategories: string[];
  recentProducts: string[];
  averageSessionDuration: number;
  totalSessions: number;
  primaryLocation?: {
    city?: string;
    region?: string;
    country?: string;
    lastSeen: Timestamp;
  };
  locations: Array<{
    city?: string;
    region?: string;
    country?: string;
    interactionCount: number;
    lastSeen: Timestamp;
  }>;
}

// Consolidated recommendation data structure
export interface RecommendationData {
  id?: string;
  userId?: string; // null for global data
  type: 'user_analytics' | 'product_popularity' | 'location_popularity' | 'category_popularity';
  data: {
    // User analytics data
    userAnalytics?: UserBehaviorAnalytics;
    
    // Product popularity data
    productPopularity?: {
      productId: string;
      viewCount: number;
      clickCount: number;
      addToCartCount: number;
      addToWishlistCount: number;
      lastUpdated: Timestamp;
    };
    
    // Location popularity data
    locationPopularity?: {
      city?: string;
      region?: string;
      country?: string;
      popularProducts: Array<{
        productId: string;
        viewCount: number;
        lastUpdated: Timestamp;
      }>;
      totalInteractions: number;
      lastUpdated: Timestamp;
    };
    
    // Category popularity data
    categoryPopularity?: {
      categoryId: string;
      categoryName: string;
      popularProducts: Array<{
        productId: string;
        viewCount: number;
        lastUpdated: Timestamp;
      }>;
      totalInteractions: number;
      lastUpdated: Timestamp;
    };
  };
  timestamp: Timestamp;
  lastUpdated: Timestamp;
}

// Lazy collection getters - only create when needed and if db is valid
const getInteractionsCollection = () => {
  if (!db) {
    throw new Error('Firebase Firestore not initialized. Check environment variables.');
  }
  return collection(db, COLLECTIONS.INTERACTIONS);
};

const getUserAnalyticsCollection = () => {
  if (!db) {
    throw new Error('Firebase Firestore not initialized. Check environment variables.');
  }
  return collection(db, COLLECTIONS.USER_ANALYTICS);
};

const getAggregatesCollection = () => {
  if (!db) {
    throw new Error('Firebase Firestore not initialized. Check environment variables.');
  }
  return collection(db, COLLECTIONS.AGGREGATES);
};

// Check if Firebase config is valid (not using placeholder)
const isFirebaseConfigValid = () => {
  // If __FIREBASE_CONFIG_ERROR__ exists, config is invalid
  return typeof (window as any).__FIREBASE_CONFIG_ERROR__ === 'undefined' && db !== null;
};

class UserTrackingService {
  private sessionId: string;
  private userId: string | null = null;
  private userLocation: UserInteraction['location'] | null = null;
  private sessionStartMs: number = Date.now();
  private viewedProducts: Set<string> = new Set();
  private lastViewTime: Map<string, number> = new Map();
  private readonly VIEW_DEBOUNCE_MS = 10000; // 10 seconds

  constructor() {
    // Generate unique session ID
    this.sessionId = this.generateSessionId();
    
    // Initialize session tracking only if Firebase config is valid
    // This prevents errors during module import
    if (isFirebaseConfigValid() && db) {
      this.trackSessionStart().catch(() => {
        // Silently fail - tracking is not critical for app functionality
      });
    }
  }

  // Set user ID when user logs in
  setUserId(userId: string) {
    this.userId = userId;
    // Load user's location from their delivery address
    this.loadUserLocationFromProfile(userId);
    this.trackUserLogin();
  }

  // Clear user ID when user logs out
  clearUserId() {
    if (this.userId) {
      this.trackUserLogout();
    }
    this.userId = null;
  }

  // Generate unique session ID
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Set user location from delivery address
  setUserLocationFromAddress(address: { city?: string; district?: string; province?: string; country?: string }) {
    this.userLocation = {
      city: address.city,
      region: address.district || address.province,
      country: address.country || 'Nepal'
    };
  }

  // Get user's delivery address from their profile
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async loadUserLocationFromProfile(userId: string): Promise<void> {
    try {
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        // Set default location for non-authenticated users
        this.userLocation = {
          city: 'Kathmandu',
          region: 'Bagmati',
          country: 'Nepal'
        };
        return;
      }
      
      if (userId !== currentUser.uid) {
        // SECURITY: Prevent IDOR - unauthorized access attempt
        // Set default location instead of throwing
        this.userLocation = {
          city: 'Kathmandu',
          region: 'Bagmati',
          country: 'Nepal'
        };
        return;
      }
      
      // Import the messaging service to get user profile
      const { messagingService } = await import('./firebaseConfig');
      const userProfile = await messagingService.getUserProfile(userId);
      
      if (userProfile?.location) {
        // Parse location string (assuming format like "Kathmandu, Bagmati, Nepal" or similar)
        const locationParts = userProfile.location.split(',').map(part => part.trim());
        this.userLocation = {
          city: locationParts[0] || 'Kathmandu',
          region: locationParts[1] || 'Bagmati',
          country: locationParts[2] || 'Nepal'
        };
      } else {
        // Set default location for Nepal
        this.userLocation = {
          city: 'Kathmandu',
          region: 'Bagmati',
          country: 'Nepal'
        };
      }
    } catch (error) {
      secureLog('warn', 'Failed to load user location from profile:', error);
      // Set default location for Nepal
      this.userLocation = {
        city: 'Kathmandu',
        region: 'Bagmati',
        country: 'Nepal'
      };
    }
  }

  // Track session start
  private async trackSessionStart() {
    // Don't track if Firebase config is invalid
    if (!isFirebaseConfigValid() || !db) {
      return;
    }
    
    try {
      this.sessionStartMs = Date.now();
      const interaction: UserInteraction = {
        userId: this.userId || 'anonymous',
        sessionId: this.sessionId,
        interactionType: 'session_start',
        pageUrl: window.location.href,
        location: this.userLocation || undefined,
        metadata: {
          userAgent: navigator.userAgent,
          referrer: document.referrer || 'direct'
        }
      };

      await this.saveInteraction(interaction);
    } catch (error: any) {
      // Silently handle blocked requests (ad blockers, extensions)
      if (error?.message?.includes('ERR_BLOCKED_BY_CLIENT') || 
          error?.code === 'permission-denied') {
        return;
      }
      secureLog('warn', 'Failed to track session start:', error);
    }
  }

  // Track user login
  private async trackUserLogin() {
    if (!this.userId) return;

    try {
      const interaction: UserInteraction = {
        userId: this.userId,
        sessionId: this.sessionId,
        interactionType: 'page_view',
        pageUrl: window.location.href,
        metadata: {
          userAgent: navigator.userAgent
        }
      };

      await this.saveInteraction(interaction);
    } catch (error) {
      secureLog('warn', 'Failed to track user login:', error);
    }
  }

  // Track user logout
  private async trackUserLogout() {
    if (!this.userId) return;

    try {
      const durationSec = Math.round((Date.now() - this.sessionStartMs) / 1000);
      const interaction: UserInteraction = {
        userId: this.userId,
        sessionId: this.sessionId,
        interactionType: 'session_end',
        pageUrl: window.location.href,
        location: this.userLocation || undefined,
        metadata: {
          duration: durationSec,
          userAgent: navigator.userAgent
        }
      };

      await this.saveInteraction(interaction);
    } catch (error: any) {
      // Silently handle blocked requests (ad blockers, extensions)
      if (error?.message?.includes('ERR_BLOCKED_BY_CLIENT') || 
          error?.code === 'permission-denied') {
        return;
      }
      secureLog('warn', 'Failed to track user logout:', error);
    }
  }

  // Save interaction to Firestore (raw log only)
  private async saveInteraction(interaction: UserInteraction): Promise<void> {
    // Don't save if Firebase config is invalid
    if (!isFirebaseConfigValid() || !db) {
      return;
    }
    
    // SECURITY: Only save interactions for authenticated users
    // Firestore security rules require authentication for interaction writes
    // This prevents permission-denied errors for unauthenticated users
    const currentUser = auth.currentUser;
    if (!currentUser) {
      // Silently skip - this is expected behavior for unauthenticated users
      return;
    }
    
    try {
      // Only write raw interaction log - Cloud Functions will handle aggregation
      const docRef = doc(getInteractionsCollection());
      
      // Clean the interaction data to remove undefined values
      const cleanInteraction = this.cleanUndefinedValues(interaction);
      
      const interactionData = {
        ...cleanInteraction,
        id: docRef.id,
        createdAt: serverTimestamp(), // Don't cast to Timestamp - keep as FieldValue
        ...(this.userLocation && { location: this.userLocation }),
        ...(cleanInteraction.location && { location: cleanInteraction.location })
      };

      await setDoc(docRef, interactionData);
    } catch (error: any) {
      // Silently handle expected errors (ad blockers, permission denied for unauthenticated users)
      const errorCode = error?.code || error?.errorCode || '';
      const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
      const errorName = String(error?.name || '').toLowerCase();
      
      // Suppress expected errors:
      // 1. Ad blockers/extensions blocking requests
      // 2. Permission denied for unauthenticated users (expected behavior)
      // 3. Network errors from blocked requests
      if (
        errorCode === 'permission-denied' ||
        errorCode === 'PERMISSION_DENIED' ||
        errorMessage.includes('permission-denied') ||
        errorMessage.includes('permission denied') ||
        errorMessage.includes('err_blocked_by_client') ||
        errorMessage.includes('blocked by client') ||
        (errorName === 'firebaseerror' && errorCode === 'permission-denied') ||
        errorMessage.includes('blocked')
      ) {
        // These are expected and should be completely silent
        // No logging needed - this is normal behavior for unauthenticated users
        return;
      }
      
      // Only log unexpected errors in development
      if (process.env.NODE_ENV === 'development') {
        secureLog('warn', 'Failed to save interaction:', error);
      }
    }
  }

  // Helper method to remove undefined values from objects
  private cleanUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanUndefinedValues(item));
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = this.cleanUndefinedValues(value);
        }
      }
      return cleaned;
    }
    
    return obj;
  }

  // Update user analytics (REMOVED - now handled by Cloud Functions)
  // This method is kept for API compatibility but does nothing
  private async updateUserAnalytics(interaction: UserInteraction): Promise<void> {
    // Analytics are now handled by Cloud Functions
    // No client-side analytics mutations
    return;
  }

  // Public tracking methods
  async trackProductView(productId: string, productTitle?: string, productPrice?: number, productCategory?: string) {
    // Debounce view events - avoid logging same product twice within 10s
    const now = Date.now();
    const lastView = this.lastViewTime.get(productId) || 0;
    
    if (now - lastView < this.VIEW_DEBOUNCE_MS) {
      return; // Skip duplicate view
    }
    
    this.lastViewTime.set(productId, now);
    this.viewedProducts.add(productId);

    const interaction: UserInteraction = {
      userId: this.userId || 'anonymous',
      sessionId: this.sessionId,
      interactionType: 'product_view',
      productId,
      pageUrl: window.location.href,
      location: this.userLocation || undefined,
      metadata: {
        productTitle,
        productPrice,
        productCategory,
        userAgent: navigator.userAgent,
        referrer: document.referrer || undefined
      }
    };

    await this.saveInteraction(interaction);
  }

  async trackProductClick(productId: string, productTitle?: string) {
    const interaction: UserInteraction = {
      userId: this.userId || 'anonymous',
      sessionId: this.sessionId,
      interactionType: 'product_click',
      productId,
      pageUrl: window.location.href,
      metadata: {
        productTitle,
        userAgent: navigator.userAgent
      }
    };

    await this.saveInteraction(interaction);
  }

  async trackCategoryBrowse(categoryId: string, categoryName: string) {
    const interaction: UserInteraction = {
      userId: this.userId || 'anonymous',
      sessionId: this.sessionId,
      interactionType: 'category_browse',
      categoryId,
      categoryName,
      pageUrl: window.location.href,
      metadata: {
        userAgent: navigator.userAgent
      }
    };

    await this.saveInteraction(interaction);
  }

  async trackSearchQuery(query: string) {
    const interaction: UserInteraction = {
      userId: this.userId || 'anonymous',
      sessionId: this.sessionId,
      interactionType: 'search_query',
      searchQuery: query,
      pageUrl: window.location.href,
      metadata: {
        userAgent: navigator.userAgent
      }
    };

    await this.saveInteraction(interaction);
  }

  async trackAddToCart(productId: string, productTitle?: string, productPrice?: number) {
    const interaction: UserInteraction = {
      userId: this.userId || 'anonymous',
      sessionId: this.sessionId,
      interactionType: 'add_to_cart',
      productId,
      metadata: {
        productTitle,
        productPrice,
        userAgent: navigator.userAgent
      }
    };

    await this.saveInteraction(interaction);
  }

  async trackAddToWishlist(productId: string, productTitle?: string) {
    const interaction: UserInteraction = {
      userId: this.userId || 'anonymous',
      sessionId: this.sessionId,
      interactionType: 'add_to_wishlist',
      productId,
      metadata: {
        productTitle,
        userAgent: navigator.userAgent
      }
    };

    await this.saveInteraction(interaction);
  }

  async trackPurchase(productId: string, productTitle?: string, productPrice?: number) {
    const interaction: UserInteraction = {
      userId: this.userId || 'anonymous',
      sessionId: this.sessionId,
      interactionType: 'purchase',
      productId,
      metadata: {
        productTitle,
        productPrice,
        userAgent: navigator.userAgent
      }
    };

    await this.saveInteraction(interaction);
  }

  // Get user behavior analytics
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async getUserAnalytics(userId: string): Promise<UserBehaviorAnalytics | null> {
    try {
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        // Allow anonymous access but return null (Firestore rules will handle permissions)
        return null;
      }
      
      if (userId !== currentUser.uid) {
        // SECURITY: Prevent IDOR - unauthorized access attempt
        return null; // Return null instead of throwing to prevent information disclosure
      }
      
      const docRef = doc(getUserAnalyticsCollection(), userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) return null;
      
      return docSnap.data() as UserBehaviorAnalytics;
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
        secureLog('warn', 'Permission denied for user analytics - user not authenticated, returning null');
        return null;
      }
      secureLog('warn', 'Failed to get user analytics:', error);
      return null;
    }
  }

  // Get user interactions for recommendations (simplified - uses analytics data)
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async getUserInteractions(userId: string, limitCount: number = 100): Promise<UserInteraction[]> {
    try {
      // SECURITY: IDOR Protection - getUserAnalytics will validate ownership
      // Get user analytics instead of separate interactions
      const analytics = await this.getUserAnalytics(userId);
      if (!analytics) return [];
      
      // Convert analytics data to interaction format for compatibility
      const interactions: UserInteraction[] = [];
      
      // Create interactions from recent products
      analytics.recentProducts.forEach(productId => {
        interactions.push({
          userId: analytics.userId,
          sessionId: 'analytics_session',
          interactionType: 'product_view',
          productId,
          location: analytics.primaryLocation ? {
            city: analytics.primaryLocation.city,
            region: analytics.primaryLocation.region,
            country: analytics.primaryLocation.country
          } : undefined
        });
      });
      
      return interactions.slice(0, limitCount);
    } catch (error) {
      secureLog('warn', 'Failed to get user interactions:', error);
      return [];
    }
  }

  // Get popular products based on interactions
  async getPopularProducts(limitCount: number = 20): Promise<{productId: string, viewCount: number}[]> {
    try {
      // Read from precomputed top products (fast)
      const topProductsRef = doc(getAggregatesCollection(), COLLECTIONS.PRODUCT_POPULARITY_TOP);
      const topProductsSnap = await getDoc(topProductsRef);
      
      if (topProductsSnap.exists()) {
        const data = topProductsSnap.data();
        return (data.products || []).slice(0, limitCount);
      }
      
      // Fallback: return empty array if no precomputed data
      return [];
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      // This is expected behavior - don't log warnings for unauthenticated users
      if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
        // Silently return empty array - this is expected for unauthenticated users
        return [];
      }
      // Only log unexpected errors in development
      if (process.env.NODE_ENV === 'development') {
        secureLog('warn', 'Failed to get popular products:', error);
      }
      return [];
    }
  }

  // Get popular products by location (simplified - uses global popular products)
  async getPopularProductsByLocation(
    city?: string, 
    region?: string, 
    limitCount: number = 20
  ): Promise<{productId: string, viewCount: number, location: string}[]> {
    try {
      // For now, use global popular products with location label
      // This eliminates the need for complex location-based queries
      const popularProducts = await this.getPopularProducts(limitCount);
      const locationLabel = city || region || 'Global';
      
      return popularProducts.map(p => ({ 
        ...p, 
        location: locationLabel 
      }));
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      // This is expected behavior - don't log warnings for unauthenticated users
      if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
        // Silently return empty array - this is expected for unauthenticated users
        return [];
      }
      // Only log unexpected errors in development
      if (process.env.NODE_ENV === 'development') {
        secureLog('warn', 'Failed to get popular products by location:', error);
      }
      return [];
    }
  }

  // Get current user location
  getCurrentUserLocation(): UserInteraction['location'] | null {
    return this.userLocation;
  }

  // Get location-based analytics
  async getLocationAnalytics(): Promise<{
    topCities: Array<{city: string, interactionCount: number}>;
    topRegions: Array<{region: string, interactionCount: number}>;
    totalLocations: number;
  }> {
    // For now, return default analytics since location data is now in user_analytics
    // This can be enhanced later with dedicated location analytics
    return {
      topCities: [
        { city: 'Kathmandu', interactionCount: 100 },
        { city: 'Pokhara', interactionCount: 50 },
        { city: 'Lalitpur', interactionCount: 30 }
      ],
      topRegions: [
        { region: 'Bagmati', interactionCount: 150 },
        { region: 'Gandaki', interactionCount: 60 },
        { region: 'Karnali', interactionCount: 20 }
      ],
      totalLocations: 6
    };
  }
}

// Export singleton instance
export const userTrackingService = new UserTrackingService();

